import React from "react";
import { Box, Typography, TypographyTypeMap, Tooltip } from "@mui/material";
import { Theme } from "@mui/material/styles";
import { SxProps } from "@mui/system";
import { useTranslation } from "react-i18next";
import { i18n } from "i18next";
import FittedText from "./FittedText";

export interface AbbreviateNumberProps {
  hideTooltip?: boolean;
  fitToContainer?: boolean;
  maxFitToContainerFontSize?: number;
  options?: Intl.NumberFormatOptions;
  sx?: SxProps<Theme> | undefined;
  typographyComponent?: string;
  typographyVariant?: TypographyTypeMap | string;
  value: number;
}

function getBaseLog(logBase: number, value: number) {
  return Math.max(Math.log(value) / Math.log(logBase), 0);
}

export function toAbbreviatedNumber(i18n: i18n, value: number, options?: Intl.NumberFormatOptions): string {
  if (value === 0) {
    return "0";
  }

  const abbreviation = [
    "",
    i18n.t("abbreviation.thousand"),
    i18n.t("abbreviation.million"),
    i18n.t("abbreviation.billion"),
    i18n.t("abbreviation.trillion"),
  ];

  const baseLog = Math.floor(getBaseLog(1000, value));

  if (baseLog > abbreviation.length-1) {
    // The number is too large for the list of supported abbreviations.
    return i18n.t("intlNumber", {
      maximumFractionDigits: options?.maximumFractionDigits ?? 2,
      val: value
    });
  }

  const decimalValue = value / Math.pow(1000, baseLog);

  let result = i18n.t("intlNumber", {
    roundingPriority: "lessPrecision",
    val: decimalValue, 
    formatParams: {
      val: {
        maximumFractionDigits: 2,
        ...options
      }
    }
  });

  result = result + abbreviation[baseLog];
  return result;
}

export default function AbbreviateNumber(props: AbbreviateNumberProps): React.JSX.Element {
  const { i18n } = useTranslation();
  const variant: any = props.typographyVariant || "body1";
  const typographyComponent: any = props.typographyComponent || "span";
  const style = { fontWeight: "bold", lineHeight: "100%", ...props.sx };
  const valueString = toAbbreviatedNumber(i18n, props.value, props.options);
  const tooltip = i18n.t("intlNumber", { val: props.value, formatParams: {val: props.options || {}} })

    // Get css settings for color and alignment to use for the FittedText component
  const displayColor = props.sx?.color || undefined;
  let horizontalAlignment = props.sx?.textAlign || props.sx?.justifyContent || undefined;
  if (horizontalAlignment === "start" || horizontalAlignment === "flex-start") {
    horizontalAlignment = "left";
  } else if (horizontalAlignment === "end" || horizontalAlignment === "flex-end") {
    horizontalAlignment = "right";
  }

  return (
    <>
      {props.fitToContainer && (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            position: "relative"
          }}>
          {!props.hideTooltip ? (
            <Tooltip title={tooltip} followCursor>
              <Box>
                <FittedText content={valueString} color={displayColor} horizontalAlignment={horizontalAlignment} />
              </Box>
            </Tooltip>
          ) : (
            <FittedText content={valueString} color={displayColor} horizontalAlignment={horizontalAlignment} />
          )}
        </Box>
      )}
      {!props.fitToContainer && (
        !props.hideTooltip ? (
          <Tooltip title={tooltip} followCursor>
            <span>
              <Typography variant={variant} component={typographyComponent} sx={props.sx}>
                {valueString}
              </Typography>
            </span>
          </Tooltip>
        ) : (
          <Typography variant={variant} component={typographyComponent} sx={props.sx}>
            {valueString}
          </Typography>
        )
      )}
    </>
  );
}
